import { create } from "zustand";
import {
  DiscoveryHost,
  OnvifCredential,
  OnvifScanMode,
  OnvifScanStatus,
  UnitScanApplyResponse,
  UnitScanCredentialRef,
  UnitScanResult,
} from "../OnvifScanUtils/onvifScanInterfaces";

interface OnvifScanState {
  // "discover" = find new cameras over a WireGuard tunnel (default, unchanged).
  // "update" = re-read the units behind the site's existing cameras and update
  // them in place (camera-unit-scan/). Set by the route wrapper.
  mode: OnvifScanMode;
  setMode: (value: OnvifScanMode) => void;

  tunnelId: number | null;
  setTunnelId: (value: number | null) => void;

  credentials: OnvifCredential[];
  setCredentials: (value: OnvifCredential[]) => void;

  // The exact credential list sent to the running/last scan, in order. Results
  // report which credential authenticated by index into this list, so onboarding
  // must resolve against it -- not against `credentials`, which the operator can
  // still edit after a scan and which includes rows the scan filtered out.
  scanCredentials: OnvifCredential[];
  setScanCredentials: (value: OnvifCredential[]) => void;

  subnet: string;
  setSubnet: (value: string) => void;

  scanStatus: OnvifScanStatus;
  setScanStatus: (value: OnvifScanStatus) => void;

  taskId: string | null;
  setTaskId: (value: string | null) => void;

  hosts: DiscoveryHost[];
  setHosts: (value: DiscoveryHost[]) => void;

  errorMessage: string;
  setErrorMessage: (value: string) => void;

  // keyed by DiscoveredCamera.key
  selectedKeys: Record<string, boolean>;
  setSelectedKeys: (value: Record<string, boolean>) => void;

  // chosen stream/profile token per camera key (defaults to the primary profile)
  selectedProfiles: Record<string, string>;
  setSelectedProfile: (key: string, token: string) => void;

  submitting: boolean;
  setSubmitting: (value: boolean) => void;

  // --- update mode (camera unit scan) ---
  // Number of cameras the scan targets and the logins it will try, from the
  // 202 response, shown while the scan runs.
  unitScanTargets: number | null;
  setUnitScanTargets: (value: number | null) => void;
  unitScanCredentialRefs: UnitScanCredentialRef[];
  setUnitScanCredentialRefs: (value: UnitScanCredentialRef[]) => void;
  unitScanResult: UnitScanResult | null;
  setUnitScanResult: (value: UnitScanResult | null) => void;
  // keyed by camera id
  unitSelectedIds: Record<number, boolean>;
  setUnitSelectedIds: (value: Record<number, boolean>) => void;
  applying: boolean;
  setApplying: (value: boolean) => void;
  applyResult: UnitScanApplyResponse | null;
  setApplyResult: (value: UnitScanApplyResponse | null) => void;

  reset: () => void;
}

const initialState = {
  mode: "discover" as OnvifScanMode,
  tunnelId: null as number | null,
  credentials: [{ username: "", password: "" }] as OnvifCredential[],
  scanCredentials: [] as OnvifCredential[],
  subnet: "",
  scanStatus: "idle" as OnvifScanStatus,
  taskId: null as string | null,
  hosts: [] as DiscoveryHost[],
  errorMessage: "",
  selectedKeys: {} as Record<string, boolean>,
  selectedProfiles: {} as Record<string, string>,
  submitting: false,
  unitScanTargets: null as number | null,
  unitScanCredentialRefs: [] as UnitScanCredentialRef[],
  unitScanResult: null as UnitScanResult | null,
  unitSelectedIds: {} as Record<number, boolean>,
  applying: false,
  applyResult: null as UnitScanApplyResponse | null,
};

export const useOnvifScanStore = create<OnvifScanState>((set) => ({
  ...initialState,

  setMode: (value) => set({ mode: value }),
  setTunnelId: (value) => set({ tunnelId: value }),
  setCredentials: (value) => set({ credentials: value }),
  setScanCredentials: (value) => set({ scanCredentials: value }),
  setSubnet: (value) => set({ subnet: value }),
  setScanStatus: (value) => set({ scanStatus: value }),
  setTaskId: (value) => set({ taskId: value }),
  setHosts: (value) => set({ hosts: value }),
  setErrorMessage: (value) => set({ errorMessage: value }),
  setSelectedKeys: (value) => set({ selectedKeys: value }),
  setSelectedProfile: (key, token) =>
    set((state) => ({
      selectedProfiles: { ...state.selectedProfiles, [key]: token },
    })),
  setSubmitting: (value) => set({ submitting: value }),
  setUnitScanTargets: (value) => set({ unitScanTargets: value }),
  setUnitScanCredentialRefs: (value) => set({ unitScanCredentialRefs: value }),
  setUnitScanResult: (value) => set({ unitScanResult: value }),
  setUnitSelectedIds: (value) => set({ unitSelectedIds: value }),
  setApplying: (value) => set({ applying: value }),
  setApplyResult: (value) => set({ applyResult: value }),

  // Keeps `mode`: the route wrapper owns it, and reset() runs on every visit.
  reset: () => set((state) => ({ ...initialState, mode: state.mode })),
}));
